/**
 * ServiceRequestDetail — merchant-side detail view for a single service
 * request. Shows:
 *   • 5-stage progress bar
 *   • Request summary (company, service, notes, attachments)
 *   • Payment block (shown when stage == FEE_COLLECTION):
 *       - Online: "ادفع الآن" button → opens checkout_url
 *       - Bank transfer: beneficiary + IBAN + reference info
 *   • Deliverables list (shown when stage == COMPLETED_AND_DISPATCHED)
 *   • Auto-refresh every 60s while mounted.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import {
  Loader2, ArrowLeft, CheckCircle, Clock, Briefcase, Building2,
  Paperclip, Receipt, Download, CreditCard, Banknote, AlertCircle,
  FileText, Copy, Upload, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_ORDER = [
  'REQUEST_RECEIVED',
  'REQUEST_ACCEPTED',
  'IN_PROGRESS',
  'FEE_COLLECTION',
  'COMPLETED_AND_DISPATCHED',
];

const STAGE_LABELS = {
  REQUEST_RECEIVED: { ar: 'تم استلام الطلب',  en: 'Received' },
  REQUEST_ACCEPTED: { ar: 'تم قبول الطلب',    en: 'Accepted' },
  IN_PROGRESS:      { ar: 'جاري التنفيذ',     en: 'In progress' },
  FEE_COLLECTION:   { ar: 'تحصيل الرسوم',     en: 'Fee due' },
  COMPLETED_AND_DISPATCHED: { ar: 'تم الانتهاء', en: 'Completed' },
};

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const refreshRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const token = localStorage.getItem('business_token');
      const res = await axios.get(`${BACKEND_URL}/api/service-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequest(res.data?.data || res.data);
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string'
        ? detail
        : (language === 'ar' ? 'تعذّر جلب تفاصيل الطلب' : 'Could not load request'));
    } finally {
      setLoading(false);
    }
  }, [id, language]);

  useEffect(() => {
    fetchDetail();
    refreshRef.current = setInterval(fetchDetail, 60000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [fetchDetail]);

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'الملف أكبر من 8 ميجا' : 'File is over 8 MB');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('business_token');
      const fd = new FormData();
      fd.append('file', file, file.name);
      await axios.post(
        `${BACKEND_URL}/api/service-requests/${id}/attachments`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(language === 'ar' ? 'تم رفع المرفق' : 'Attachment uploaded');
      await fetchDetail();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string'
        ? detail
        : (language === 'ar' ? 'فشل رفع الملف' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('business_token');
      const resp = await fetch(
        `${BACKEND_URL}/api/service-requests/${id}/files/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('download-failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `file-${fileId}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(language === 'ar' ? 'فشل التنزيل' : 'Download failed');
    }
  };

  const copy = (text, msg) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(msg || (language === 'ar' ? 'تم النسخ' : 'Copied'));
    } catch {}
  };

  const stage = request?.stage || request?.status || 'REQUEST_RECEIVED';
  const stageIndex = STAGE_ORDER.indexOf(stage);

  if (loading) {
    return (
      <div className="min-h-screen cgm-page flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#38BDF8]" />
      </div>
    );
  }
  if (error || !request) {
    return (
      <div className="min-h-screen cgm-page">
        <Navbar onLogout={() => {}} />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="cgm-ai-card !border-red-300 !bg-red-50 dark:!bg-red-500/10">
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canAddAttachments = stage !== 'FEE_COLLECTION' && stage !== 'COMPLETED_AND_DISPATCHED';
  const payment = request.payment || null;
  const deliverables = request.deliverables || [];
  const attachments = request.attachments || [];
  const timeline = request.timeline || [];

  return (
    <div className="min-h-screen cgm-page">
      <Navbar onLogout={() => {
        navigate('/', { replace: true });
        localStorage.removeItem('business_token');
        localStorage.removeItem('business_user');
        window.dispatchEvent(new Event('userLoggedOut'));
      }} />
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <main className={`flex-1 min-w-0 transition-all duration-300 pb-10 ${sidebarOpen ? (language === 'ar' ? 'lg:mr-64' : 'lg:ml-64') : ''}`}>
          {/* Hero */}
          <section className="cgm-hero py-5 sm:py-6 mb-5">
            <div className="cgm-hero-content max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <button
                type="button"
                onClick={() => navigate('/service-requests')}
                className="inline-flex items-center text-white/80 hover:text-white mb-3 text-sm transition-colors"
                data-testid="sr-back"
              >
                <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
                {language === 'ar' ? 'العودة إلى طلباتي' : 'Back to my requests'}
              </button>
              <div className="flex items-center gap-3 cgm-reveal">
                <div className="cgm-icon-glow cgm-breathe !p-2.5">
                  <Briefcase className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight" data-testid="sr-detail-no">
                    {request.request_no || request.id?.slice(0, 10)}
                  </h1>
                  <p className="text-white/80 text-sm">
                    {language === 'ar'
                      ? (request.main_service_name_ar || request.main_service_name_en)
                      : (request.main_service_name_en || request.main_service_name_ar)}
                    {' › '}
                    <span className="font-semibold">
                      {language === 'ar'
                        ? (request.sub_service_name_ar || request.sub_service_name_en)
                        : (request.sub_service_name_en || request.sub_service_name_ar)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
            {/* Progress bar */}
            <div className="cgm-ai-card-elevated">
              <h2 className={`text-sm font-black uppercase tracking-wider mb-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {language === 'ar' ? 'المراحل' : 'Progress'}
              </h2>
              <div className="flex items-start gap-1 overflow-x-auto">
                {STAGE_ORDER.map((code, i) => {
                  const done = i < stageIndex;
                  const current = i === stageIndex;
                  return (
                    <div key={code} className="flex-1 min-w-[90px] flex flex-col items-center">
                      <div className={`relative h-9 w-9 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${
                        done
                          ? 'bg-gradient-to-br from-[#38BDF8] to-[#F97316] text-white'
                          : current
                            ? 'bg-gradient-to-br from-[#38BDF8] to-[#F97316] text-white cgm-breathe'
                            : (isDarkMode ? 'bg-white/10 text-slate-500' : 'bg-slate-200 text-slate-500')
                      }`}>
                        {done ? <CheckCircle className="h-4 w-4" /> : (i + 1)}
                      </div>
                      <p className={`mt-2 text-[10px] sm:text-xs text-center font-semibold ${
                        done || current ? (isDarkMode ? 'text-white' : 'text-slate-900') : (isDarkMode ? 'text-slate-500' : 'text-slate-400')
                      }`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                        {language === 'ar' ? STAGE_LABELS[code].ar : STAGE_LABELS[code].en}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment block */}
            {payment && payment.required && (
              <div className="cgm-ai-card-featured" data-testid="sr-payment">
                <h2 className={`text-sm font-black uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {language === 'ar' ? 'الدفع' : 'Payment'}
                </h2>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <p className={`text-xs font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {language === 'ar' ? 'رقم الفاتورة' : 'Invoice'}
                    </p>
                    <p className={`font-mono text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {payment.invoice_no || '—'}
                    </p>
                  </div>
                  <div className="text-end">
                    <p className={`text-xs font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {language === 'ar' ? 'المطلوب' : 'Amount Due'}
                    </p>
                    <p className="cgm-stat-value-ai !text-2xl">
                      {Number(payment.total || 0).toFixed(2)} AED
                    </p>
                    {Number(payment.tax_amount || 0) > 0 && (
                      <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {language === 'ar' ? `شامل ضريبة ${Number(payment.tax_amount).toFixed(2)}` : `incl. tax ${Number(payment.tax_amount).toFixed(2)}`}
                      </p>
                    )}
                  </div>
                </div>

                {payment.status === 'paid' ? (
                  <div className={`rounded-lg border p-3 flex items-center gap-2 ${
                    isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  }`}>
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm font-semibold">
                      {language === 'ar' ? 'مدفوعة' : 'Paid'}
                    </span>
                  </div>
                ) : payment.method === 'online' && payment.checkout_url ? (
                  <a
                    href={payment.checkout_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cgm-cta-primary w-full"
                    data-testid="sr-pay-online"
                  >
                    <CreditCard className="h-4 w-4" />
                    {language === 'ar' ? 'ادفع الآن' : 'Pay now'}
                  </a>
                ) : payment.method === 'online' ? (
                  <div className={`rounded-lg border p-3 flex items-center gap-2 ${
                    isDarkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'
                  }`}>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm">
                      {language === 'ar' ? 'جارٍ تجهيز رابط الدفع…' : 'Preparing payment link…'}
                    </span>
                  </div>
                ) : payment.method === 'bank_transfer' && payment.bank_transfer ? (
                  <div>
                    <div className={`rounded-lg border p-4 ${
                      isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Banknote className="h-5 w-5 text-[#38BDF8]" />
                        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {language === 'ar' ? 'بيانات الحساب البنكي' : 'Bank account details'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <BankRow label={language === 'ar' ? 'المستفيد' : 'Beneficiary'} value={payment.bank_transfer.beneficiary_name} isDarkMode={isDarkMode} onCopy={copy} />
                        <BankRow label={language === 'ar' ? 'البنك' : 'Bank'} value={payment.bank_transfer.bank_name} isDarkMode={isDarkMode} onCopy={copy} />
                        <BankRow label="IBAN" value={payment.bank_transfer.iban} isDarkMode={isDarkMode} onCopy={copy} mono />
                        <BankRow label={language === 'ar' ? 'رقم الحساب' : 'Account #'} value={payment.bank_transfer.account_number} isDarkMode={isDarkMode} onCopy={copy} mono />
                        <BankRow label="SWIFT" value={payment.bank_transfer.swift_code} isDarkMode={isDarkMode} onCopy={copy} mono />
                        <BankRow label={language === 'ar' ? 'الفرع' : 'Branch'} value={payment.bank_transfer.branch} isDarkMode={isDarkMode} onCopy={copy} />
                      </div>
                    </div>
                    <p className={`text-xs mt-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {language === 'ar'
                        ? `يُرجى تضمين رقم الفاتورة ${payment.invoice_no || ''} في مرجع التحويل.`
                        : `Please include invoice number ${payment.invoice_no || ''} in the transfer reference.`}
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Deliverables */}
            {deliverables.length > 0 && (
              <div className="cgm-ai-card-elevated" data-testid="sr-deliverables">
                <h2 className={`text-sm font-black uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {language === 'ar' ? 'ملفات التسليم' : 'Deliverables'}
                </h2>
                <ul className="space-y-2">
                  {deliverables.map((d) => (
                    <li key={d.file_id || d.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg ${
                      isDarkMode ? 'bg-white/5' : 'bg-slate-50'
                    }`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-500 flex-shrink-0">
                          <FileText className="h-4 w-4" />
                        </div>
                        <span className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {d.name || d.filename || d.file_id}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadFile(d.file_id || d.id, d.name || d.filename)}
                        className="cgm-btn-secondary !text-xs !py-1.5 !px-3 flex items-center gap-1"
                        data-testid={`sr-download-${d.file_id || d.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                        {language === 'ar' ? 'تنزيل' : 'Download'}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Summary + attachments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="cgm-ai-card">
                <h2 className={`text-sm font-black uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {language === 'ar' ? 'تفاصيل الطلب' : 'Request details'}
                </h2>
                <div className="space-y-3 text-sm">
                  <SummaryRow icon={Building2} label={language === 'ar' ? 'الشركة' : 'Company'}
                    value={language === 'ar' ? (request.company_name_ar || request.company_name_en) : (request.company_name_en || request.company_name_ar)}
                    isDarkMode={isDarkMode} />
                  <SummaryRow icon={Briefcase} label={language === 'ar' ? 'الخدمة' : 'Service'}
                    value={`${language === 'ar' ? (request.sub_service_name_ar || request.sub_service_name_en) : (request.sub_service_name_en || request.sub_service_name_ar)}`}
                    isDarkMode={isDarkMode} />
                  <SummaryRow icon={Clock} label={language === 'ar' ? 'تاريخ الإرسال' : 'Submitted'}
                    value={new Date(request.created_at || request.createdAt || Date.now()).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
                    isDarkMode={isDarkMode} />
                  {request.notes && (
                    <div>
                      <p className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {language === 'ar' ? 'الملاحظات' : 'Notes'}
                      </p>
                      <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                        {request.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="cgm-ai-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className={`text-sm font-black uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {language === 'ar' ? 'المرفقات' : 'Attachments'}
                  </h2>
                  {canAddAttachments && (
                    <label className="cgm-btn-secondary !text-xs !py-1.5 !px-3 flex items-center gap-1 cursor-pointer">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      {language === 'ar' ? 'إضافة' : 'Add'}
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => { if (e.target.files?.[0]) uploadAttachment(e.target.files[0]); e.target.value = ''; }}
                        data-testid="sr-attach-input"
                      />
                    </label>
                  )}
                </div>
                {attachments.length === 0 ? (
                  <p className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {language === 'ar' ? 'لا توجد مرفقات بعد.' : 'No attachments yet.'}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {attachments.map((a) => (
                      <li key={a.file_id || a.id} className={`flex items-center justify-between gap-2 p-2 rounded-lg text-xs ${
                        isDarkMode ? 'bg-white/5' : 'bg-slate-50'
                      }`}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <Paperclip className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{a.name || a.filename || a.file_id}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => downloadFile(a.file_id || a.id, a.name || a.filename)}
                          className="text-[#38BDF8] hover:underline flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          {language === 'ar' ? 'تنزيل' : 'Download'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Timeline */}
            {timeline.length > 0 && (
              <div className="cgm-ai-card">
                <h2 className={`text-sm font-black uppercase tracking-wider mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {language === 'ar' ? 'سجل الأحداث' : 'Timeline'}
                </h2>
                <ol className="space-y-2 text-sm">
                  {timeline.map((e, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="h-2 w-2 rounded-full bg-[#38BDF8] mt-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {language === 'ar'
                            ? (STAGE_LABELS[e.stage]?.ar || e.title_ar || e.title)
                            : (STAGE_LABELS[e.stage]?.en || e.title_en || e.title)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(e.at || e.timestamp || e.created_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}
                        </p>
                        {e.note && (
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {e.note}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value, isDarkMode }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
      <div className="min-w-0">
        <p className={`text-[11px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
        <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value || '—'}</p>
      </div>
    </div>
  );
}

function BankRow({ label, value, isDarkMode, onCopy, mono }) {
  return (
    <div>
      <p className={`text-[11px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
      <div className="flex items-center gap-2 justify-between">
        <span className={`${mono ? 'font-mono' : ''} ${isDarkMode ? 'text-white' : 'text-slate-900'} truncate`}>{value || '—'}</span>
        {value && (
          <button type="button" onClick={() => onCopy(value)} className="opacity-50 hover:opacity-100">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
